import { NewNudge } from '@/models/nudge-config/newNudge';
import { myGooruAPI } from '@/providers/apis/my-gooru/my-gooru';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { ref, watch, watchEffect, nextTick, reactive, onUnmounted } from 'vue';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

@Component({
  name: 'create-new-nudge',
})
export default class CreateNewNudge extends Vue {
  @Prop({ default: undefined })
  private selectedTenant: any;
  @Prop()
  private isEdit!: boolean;
  @Prop({ default: undefined })
  private updatingNudge: any;
  @Prop()
  private clearUpdatingNudge!: () => void;
  @Prop()
  private getTenantNudges!: (tenant: any) => void;

  private nudgeList: any;
  private newNudge: NewNudge = {
    nudge_id: {
      id: '',
      icon: '',
    },
    persona: '',
    page_type: '',
    is_access: false,
    rank: undefined,
    tenant_id: '',
    action: [],
    nudge_mapping_id: 0,
  };
  private nudgeErrorMsg: any = {
    nudge_id: '',
    persona: '',
    page_type: '',
    tenant_id: '',
  };
  private isCreated: boolean = false;
  private isUpdated: boolean = false;
  private selectedActions: string[] = [];
  private tenantList: any = [];
  private tenantListTotalCount: any = '';
  private searchTeantList = '';
  private offset: number = 0;
  private limit: number = 100;
  private noDataAvailable: boolean = false;
  private searchLoader = false;
  private countCheck = 0;
  private isLoading: boolean = false;
  private dropdown = ref(null);
  private errorMsg = false;
  private pageLoader = false;
  private copyOfUpdatingNudge: any = null;
  private showDropdown = true;

  private errorObject = {
    existMapping: false,
    creationError: false,
    updatingError: false,
  };

  private async created() {
    this.newNudge.tenant_id = this.selectedTenant.id;

    if (this.isEdit && this.updatingNudge) {
      this.newNudge = this.cloneNudgeDetails(this.updatingNudge);
      this.copyOfUpdatingNudge = this.cloneNudgeDetails(this.updatingNudge);
    }
  }

  private cloneNudgeDetails(updateNudge: any) {
    const nudge: NewNudge = {
    nudge_id : {
      id: updateNudge.nudge_id,
      icon: updateNudge.thumbnail,
    },
    persona : updateNudge.persona,
    page_type : updateNudge.page_type,
    is_access : updateNudge.is_access,
    rank : updateNudge.rank,
    nudge_mapping_id : parseInt(updateNudge.nudge_mapping_id, 10),
    tenant_id : updateNudge.tenant_id,
    action : updateNudge.action
      ? updateNudge.action.filter((action: any) => action !== '' || action !== '')
      : [],
    };
    return nudge;
  }

  private async mounted() {
    await nextTick();
    this.getNudgeProperties();
    this.getTenantList();
  }

  private formattedSelectedActions() {
    if (this.newNudge.action.length === 0) {
      return {
        text: 'Select action item',
        remainingCount: 0,
      };
    }

    const maxLength = 20;
    const selectedText = this.newNudge.action.join(' • ');

    if (selectedText.length > maxLength) {
      const visibleActions = [];
      let charCount = 0;

      for (const action of this.newNudge.action) {
        if (charCount + action.length > maxLength) {
          break;
        }
        visibleActions.push(action);
        charCount += action.length + 3;
      }

      const remainingCount =
        this.newNudge.action.length - visibleActions.length;
      charCount = 0;
      return {
        text: visibleActions.slice(0, 3).join(' • '),
        remainingCount: `• +${remainingCount} more`,
      };
    }

    return {
      text: selectedText,
      remainingCount: 0,
    };
  }

  private closePopUp() {
    this.updatingNudge = '';
    this.resetNudgeForm();
    this.clearUpdatingNudge();

    this.$bvModal.hide('create-new-nudge');
  }

  private resetNudgeForm() {
    this.newNudge = {
      nudge_id: { id: '', icon: '' },
      persona: '',
      page_type: '',
      is_access: false,
      rank: undefined,
      tenant_id: '',
      action: [],
      nudge_mapping_id: 0,
    };
    this.selectedActions = [];
  }

  private async getTenantList() {
    const offset = this.offset;
    const limit = this.limit;
    const name = this.searchTeantList;
    const params: any = {
      offset,
      limit,
      name,
    };

    await myGooruAPI.getTenantList(params).then((response: any) => {
      this.countCheck++;
      this.searchLoader = false;
      this.isLoading = false;
      if (this.searchTeantList.length > 3) {
        this.tenantList = [];
      }
      this.tenantListTotalCount = response.data.total_count;
      this.tenantList.push(response.data.list);
      if (this.tenantList[0].length === 0) {
        this.noDataAvailable = true;
      }
    });
  }

  private clearSelectedTenant() {
    this.selectedTenant = undefined;
  }

  private async getNudgeProperties() {
    this.pageLoader = true;
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;

    await myGooruAPI.fetchNudgeProperties().then((response: any) => {
      this.pageLoader = false;

      const nudgePropertiesList: any = response.data;
      if (nudgePropertiesList) {
        nudgePropertiesList.nudge_ids.forEach((nudge: any) => {
          if (nudge.icon && nudge.icon.icon) {
            nudge.icon.icon = contentCdnUrl + nudge.icon.icon;
          } else {
            nudge.icon = {
              icon: `${window.location.origin}${DEFAULT_IMAGES_PATH.assessment}`,
            };
          }
        });
      }
      this.nudgeList = nudgePropertiesList;
    });
  }

  private async createNudge(newNudge: NewNudge) {
    if (!this.validateNudge(this.newNudge)) {
      return;
    }

    const payload = this.buildNudgePayload(newNudge);
    const response: any = await myGooruAPI.createNudgeMappig(payload);

    if (response.success === false) {
      if (response.status === 409) {
        this.errorObject.existMapping = true;
      } else {
        this.errorObject.creationError = true;
      }
      return;
    }
    this.isCreated = true;

    setTimeout(() => {
      this.getTenantNudges(this.selectedTenant);
      this.closePopUp();
      this.isCreated = false;
      this.errorObject.existMapping = false;
      this.errorObject.creationError = false;
    }, 1500);
  }

  private async updateNudge(updateNudge: NewNudge) {
    if (!this.validateNudge(this.newNudge)) { return; }
    const nudgeMappingId: number = updateNudge.nudge_mapping_id;

    if (nudgeMappingId === 0) {
      return;
    }
    const payload = this.getUpdatedFields(this.copyOfUpdatingNudge, updateNudge);
    if (payload.hasOwnProperty('nudge_id')) {
      payload.nudge_id = payload.nudge_id.id;
    }
    const response: any = await myGooruAPI.updateNudgeMapping(nudgeMappingId, payload);

    if (response.success === false) {
      if (response.status === 409) {
        this.errorObject.existMapping = true;
      } else {
        this.errorObject.updatingError = true;
      }
      return;
    }
    this.isUpdated = true;

    setTimeout(() => {
      this.getTenantNudges(this.selectedTenant);
      this.closePopUp();
      this.isUpdated = false;
      this.errorObject.existMapping = false;
      this.errorObject.updatingError = false;
    }, 1500);
  }

  private getCamaleCaseText(text: string) {
    if (!text) { return ''; }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private handleScroll(event: Event) {
    const target = event.target as HTMLElement;
    const roundOffValue = Math.trunc(target.scrollHeight - target.scrollTop);
    const scrollBottom = roundOffValue === target.clientHeight;
    const pageLimitCount = this.tenantListTotalCount / this.limit;
    if (scrollBottom && this.countCheck < pageLimitCount) {
      this.offset = this.offset + 100;
      this.isLoading = true;
      this.getTenantList();
    }
  }

  private validateNudge(nudge: NewNudge): boolean {
    this.nudgeErrorMsg = {
      nudge_id: '',
      persona: '',
      page_type: '',
      tenant_id: '',
    };

    let isValid = true;

    if (!nudge.nudge_id || !nudge.nudge_id.id) {
      this.nudgeErrorMsg.nudge_id = 'Nudge ID is required';
      isValid = false;
    }

    if (!nudge.tenant_id) {
      this.nudgeErrorMsg.tenant_id = 'Tenant ID is required';
      isValid = false;
    }

    if (!nudge.persona) {
      this.nudgeErrorMsg.persona = 'Persona is required';
      isValid = false;
    }

    if (!nudge.page_type) {
      this.nudgeErrorMsg.page_type = 'Page Type is required';
      isValid = false;
    }

    return isValid;
  }

  private buildNudgePayload(nudge: NewNudge) {
    return {
      nudge_id: nudge.nudge_id.id,
      tenant_id: nudge.tenant_id,
      persona: nudge.persona,
      page_type: nudge.page_type,
      is_access: nudge.is_access,
      rank: nudge.rank,
      action: [...nudge.action],
    };
  }

  private onActionChange(action: string, e: any) {
    if (e) {
      this.newNudge.action.push(action);
    } else {
      this.newNudge.action = this.newNudge.action.filter(
        (item: string) => item !== action,
      );
    }
  }

  private clearSelectedActions() {
    this.newNudge.action = [];
    this.selectedActions = [];
    this.uncheckAllCheckboxes();
  }

  private uncheckAllCheckboxes() {
    this.newNudge.action = [];
    this.selectedActions = [];

    this.$nextTick(() => {
      const container = this.$refs.checkboxContainer as HTMLElement;

      if (container) {
        const checkboxes = container.querySelectorAll('input[type=\'checkbox\']');
        checkboxes.forEach((checkbox) => {
          (checkbox as HTMLInputElement).checked = false;
        });
      }
    });
  }

  private searchTeant() {
    if (this.searchTeantList.length > 3) {
      this.errorMsg = false;
      this.offset = 0;
      this.limit = 100;
      this.searchLoader = true;
      this.getTenantList();
    } else {
      this.getTenantList();
    }
  }
  private searchTeanantListClear() {
    this.searchTeantList = '';
    this.offset = 0;
    this.limit = 100;

    this.getTenantList();
  }

  private toggleAction(action: string) {
    const isChecked = this.newNudge.action.includes(action);
    this.onActionChange(action, !isChecked);
  }

  private getUpdatedFields(nudge: any, updatingNudge: any) {
    const updatedFields: any = {};

    for (const key in nudge) {
      if (updatingNudge.hasOwnProperty(key)) {
        const newValue = updatingNudge[key] === undefined ? null : updatingNudge[key];
        const oldValue = nudge[key];

        const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val);
        const isArray = (val: any) => Array.isArray(val);
        if (isObject(oldValue) && isObject(newValue)) {
          const nestedChanges: any = this.getUpdatedFields(oldValue, newValue);
          if (Object.keys(nestedChanges).length > 0) {
            updatedFields[key] = nestedChanges;
          }
        } else if (isArray(oldValue) && isArray(newValue)) {
          // Check if arrays are different
          const arraysAreDifferent =
            oldValue.length !== newValue.length ||
            JSON.stringify(oldValue) !== JSON.stringify(newValue);
          if (arraysAreDifferent) {
            updatedFields[key] = newValue;
          }
        } else if (oldValue !== newValue) {
          updatedFields[key] = newValue;
        }
      }
    }
    return updatedFields;
  }

  private handleDropDown(key: string, property: any) {
    this.showDropdown = false;
    switch (key) {
      case 'tenant':
        this.newNudge.tenant_id = property.id;
        this.selectedTenant = property;
        break;
      case 'rank':
        this.newNudge.rank = property;
        break;
      case 'persona':
        this.newNudge.persona = property;
        break;
      case 'page_type':
        this.newNudge.page_type = property;
        break;
      default:
        this.newNudge.nudge_id.id = property.nudge_id;
        this.newNudge.nudge_id.icon = property.icon.icon;
        break;
    }
    setTimeout(() => {
      this.showDropdown = true;
    }, 500);
  }

  private resetErrorObject() {
    this.errorObject.existMapping = false;
    this.errorObject.creationError = false;
    this.errorObject.updatingError = false;
  }
}
